// import { AppThunk } from 'redux/store/createRootReducer'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { moduleName } from './constants'
import { IUserState, IUser, IUpdateUser } from './types'
import { fetchUser as fetchUserAPI } from 'features/Auth/api'
import { cleanUpPrivateStorage } from 'helper/common'
import { resetHealthSystemsListAction } from '../healthSystemsList/slice'
import { resetCommunityInfoAction } from '../chapterInfo/slice'
import { resetLoginAction } from '../login/slice'
import { reset as resetChatsAction } from '../chats/slice'
import { reset as resetChatSocketAction } from '../chatSocket/slice'
import { setLoading } from '../common/slice'
import { routes } from '../../../router'
import history from 'router/history'
import { getUserContractCategories } from '../../../features/UserContractCategories/api'
import { ROLES } from '../../../features/Permission'
import { AxiosResponse } from 'axios'
import { AppThunk } from '../../type'

const initialState: IUserState = {
  user: {
    uuid: '',
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: '',
    community: '',
    timezone: {
      uuid: '',
      name: '',
      offset: ''
    },
    community_level: false
  },
  isUserLoading: false,
  isUserLoaded: false,
  error: null,
  token: null
}

const userSlice = createSlice({
  name: moduleName,
  initialState,
  reducers: {
    getUserStart(state: IUserState) {
      state.isUserLoading = true
      state.error = null
    },
    setUserToken(state: IUserState, action: PayloadAction<{ token: string }>) {
      const { token } = action.payload
      state.token = token
    },
    getUserFinish(state: IUserState) {
      state.isUserLoading = false
      state.isUserLoaded = true
    },
    getUserError(state: IUserState, action: PayloadAction<{ error: string }>) {
      const { error } = action.payload
      state.error = error
    },
    setUserAction(state: IUserState, action: PayloadAction<{ user: IUser }>) {
      const { user } = action.payload
      state.user = user
    },
    updateUserAction(
      state: IUserState,
      action: PayloadAction<{ updatedUser: IUpdateUser }>
    ) {
      const { updatedUser } = action.payload
      state.user = {
        ...(state.user || {}),
        ...updatedUser
      }
    },
    resetUserAction(state: IUserState) {
      Object.keys(initialState).forEach(
        (key) => ((state[key] as IUserState[typeof key]) = initialState[key])
      )
    }
  }
})

export default userSlice.reducer
export const {
  getUserStart,
  getUserError,
  getUserFinish,
  setUserAction,
  updateUserAction,
  resetUserAction,
  setUserToken
} = userSlice.actions

export const fetchUser =
  (
    completedCallback?: (user: IUser) => void,
    failedCallback?: (status?: number) => void
  ): AppThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      dispatch(getUserStart())
      const result = await fetchUserAPI()
      if (result) {
        let user = result.data as IUser
        const community_level =
          user.role === ROLES.CONTRACT_STEWARD ||
          user.role === ROLES.VICE_PRESIDENT
            ? await getUserContractCategories(user.uuid, {})
            : false
        user = {
          ...user,
          community_level:
            community_level &&
            Boolean(
              community_level.data.results.find(
                (result) => !!result.community_level
              )
            )
        }
        dispatch(setUserAction({ user }))
        if (completedCallback) {
          completedCallback(user)
        }
      }
    } catch (e) {
      const err = e as AxiosResponse
      if (err?.status === 401 && err?.data?.code === 'token_not_valid') {
        dispatch(LogOutAction())
        history.push(routes.login)
      } else {
        dispatch(getUserError({ error: err?.data.message as string }))
      }
      if (failedCallback) failedCallback(err?.status)
    } finally {
      dispatch(getUserFinish())
      dispatch(setLoading(false))
    }
  }

export const LogOutAction = (): AppThunk => (dispatch) => {
  cleanUpPrivateStorage()
  dispatch(resetUserAction())
  dispatch(resetHealthSystemsListAction())
  dispatch(resetCommunityInfoAction())
  dispatch(resetLoginAction())
  dispatch(resetChatsAction())
  dispatch(resetChatSocketAction())
}
